/* Scoped Pearl gallery loading and Pearl Art grid sizing. */
(() => {
  'use strict';
  const gallerySelector = 'img[data-isuvio-pearl-gallery]';
  const cardSelector = 'img[data-isuvio-pearl-card]';
  const initialized = new WeakSet();

  const watched = new WeakMap();
  function watch(image) {
    if (watched.has(image)) return;
    const source = {src: image.dataset.pearlSrc || image.getAttribute('src'), srcset: image.dataset.pearlSrcset || image.getAttribute('srcset')};
    watched.set(image, source);
    const status = document.createElement('div');
    status.className = 'isuvio-pearl-image-status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    const message = document.createElement('span');
    message.textContent = 'Loading image…';
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'isuvio-pearl-image-retry';
    retry.textContent = 'Image couldn’t load. Retry';
    status.append(message, retry);
    image.parentElement.appendChild(status);
    const ready = () => {
      if (image.dataset.pearlSrc || !image.complete || image.naturalWidth < 1 || !/^https?:/.test(image.currentSrc)) return;
      image.dataset.pearlState = 'ready';
      image.setAttribute('aria-busy', 'false');
    };
    image.addEventListener('load', ready);
    image.addEventListener('error', () => {
      image.dataset.pearlState = 'error';
      image.setAttribute('aria-busy', 'false');
    });
    retry.addEventListener('click', event => {
      event.preventDefault(); event.stopPropagation();
      image.dataset.pearlState = 'loading';
      image.setAttribute('aria-busy', 'true');
      image.dataset.pearlSrc = source.src;
      image.dataset.pearlSrcset = source.srcset || '';
      // Reset the failed responsive source before requesting the same image again.
      image.removeAttribute('srcset');
      image.removeAttribute('src');
      requestAnimationFrame(() => load(image));
    });
    image.dataset.pearlState = image.dataset.pearlSrc ? 'pending' : 'loading';
    if (!image.dataset.pearlSrc) ready();
  }

  function load(image) {
    if (!image || !image.dataset.pearlSrc) return;
    watch(image);
    image.dataset.pearlState = 'loading';
    image.setAttribute('aria-busy', 'true');
    image.sizes = `${Math.ceil(image.getBoundingClientRect().width || image.parentElement.clientWidth || 360)}px`;
    image.srcset = image.dataset.pearlSrcset;
    image.src = image.dataset.pearlSrc;
    delete image.dataset.pearlSrc;
    delete image.dataset.pearlSrcset;
  }

  function initializeGallery(gallery) {
    if (initialized.has(gallery)) return;
    // If the theme fails to initialize, the HTML primary and noscript remain usable.
    if (!window.Flickityt4s?.data) return;
    initialized.add(gallery);
    const primary = gallery.querySelector('[data-isuvio-pearl-primary]');
    gallery.querySelectorAll(gallerySelector).forEach(watch);
    let revision = 0;
    const instance = () => window.Flickityt4s.data(gallery);
    const selected = () => gallery.querySelector('[data-main-slide].is-selected:not(.is--media-hide) ' + gallerySelector);
    const complete = image => image?.complete && image.naturalWidth > 1 && !image.dataset.pearlSrc && /^https?:/.test(image.currentSrc);

    function stable() {
      const turn = ++revision;
      // Ready/select can occur during the same initialization stack. Recheck after paint.
      requestAnimationFrame(() => requestAnimationFrame(() => {
        const carousel = instance();
        const image = selected();
        if (turn !== revision || !gallery.isConnected || !carousel?.isInitActivated || carousel.isAnimating || !image) return;
        // Do not reveal a transient non-primary selection during cold initialization.
        if (!gallery.hasAttribute('data-pearl-gallery-ready') && image !== primary) return;
        gallery.setAttribute('data-pearl-gallery-ready', '');
        if (!complete(primary) || !complete(image)) return;
        const images = [...gallery.querySelectorAll('[data-main-slide]:not(.is--media-hide)')]
          .map(slide => slide.querySelector(gallerySelector)).filter(Boolean);
        const index = images.indexOf(image);
        if (index >= 0 && images.length > 1) load(images[(index + 1) % images.length]);
      }));
    }
    function activate() {
      ++revision;
      const image = selected();
      // Selection events load the requested slide; prefetch still waits for settle.
      if (instance()?.isInitActivated && gallery.hasAttribute('data-pearl-gallery-ready')) load(image);
      stable();
    }
    let bound;
    function bindCarousel() {
      const carousel = instance();
      if (!carousel || carousel === bound) return;
      bound = carousel;
      // The theme calls jQuery.off('select.flickityt4s') during variant setup.
      // Listen directly to Flickity so that cleanup cannot remove image loading.
      carousel.on('ready', activate);
      carousel.on('select', activate);
      carousel.on('settle', stable);
    }
    // Also cover carousel construction/replacement and thumbnail-driven selection.
    new MutationObserver(records => {
      if (!records.some(record => record.type === 'childList' || record.target === gallery || record.target.matches('[data-main-slide]'))) return;
      bindCarousel();
      activate();
    }).observe(gallery, {subtree: true, childList: true, attributes: true, attributeFilter: ['class']});
    bindCarousel();
    gallery.addEventListener('load', event => {
      if (event.target === primary || event.target === selected()) stable();
    }, true);
    // The theme may have initialized before this deferred script executes.
    if (instance()?.isInitActivated) activate();
  }

  function resizeCard(image) {
    const width = Math.ceil(image.getBoundingClientRect().width);
    if (!width) return;
    const sizes = `${image.loading === 'lazy' ? 'auto, ' : ''}${width}px`;
    if (image.sizes !== sizes) image.sizes = sizes;
  }
  // Native image discovery/loading does not depend on this optional size refinement.
  const cards = 'ResizeObserver' in window ? new ResizeObserver(entries => {
    entries.forEach(entry => resizeCard(entry.target));
  }) : null;
  function scan() {
    document.querySelectorAll(gallerySelector).forEach(image => {
      const gallery = image.closest('[data-main-media]');
      if (gallery) initializeGallery(gallery);
    });
    document.querySelectorAll(cardSelector).forEach(image => {
      if (initialized.has(image)) return;
      initialized.add(image);
      resizeCard(image);
      if (cards) cards.observe(image);
    });
  }
  scan();
  if (!cards) window.addEventListener('resize', () => document.querySelectorAll(cardSelector).forEach(resizeCard), {passive: true});
  const main = document.getElementById('MainContent');
  if (main) new MutationObserver(records => {
    records.forEach(record => record.removedNodes.forEach(node => {
      if (node.nodeType === 1) {
        if (node.matches(cardSelector)) cards?.unobserve(node);
        node.querySelectorAll(cardSelector).forEach(image => cards?.unobserve(image));
      }
    }));
    if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1))) scan();
  }).observe(main, {subtree: true, childList: true});
})();
